import type { FC } from "react";

import type { IDynamicFilterRule } from "@similarweb/pro-logic-components";
import { MultiIncludeExcludeFilter } from "@similarweb/pro-logic-components";

import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import {
    ADD_RULE_TEXT_KEY,
    AUTOCOMPLETE_MAX_ITEMS_BANNER_TEXT_KEY,
    CHIP_SEPARATOR_TEXT_KEY,
    MAX_ITEMS_PER_RULE_BANNER_TEXT_KEY,
    MAX_KEYWORDS_PER_RULE,
    MAX_RULES,
    MAX_RULES_BANNER_TEXT_KEY,
    multiIncludeExcludeType,
    PRIMARY_BUTTON_TEXT_KEY,
    RULE_SEPARATOR_TEXT_KEY,
    SECONDARY_BUTTON_TEXT_KEY,
} from "app/components/multi-include-exclude-filter/consts";
import { FilterInputField } from "app/components/multi-include-exclude-filter/FilterInputField";
import { TextFieldComponent } from "app/components/multi-include-exclude-filter/TextFieldComponent";
import { getDropdownOptions } from "app/components/multi-include-exclude-filter/utils";
import { allTrackers } from "app/services/track/track";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const typeConfigMap = {
    [multiIncludeExcludeType.KEYWORD]: {
        sectionTitleKey: "home.dashboards.wizard.filters.multiIncludeExcludeKeywords",
        textFieldComponent: TextFieldComponent,
    },
    [multiIncludeExcludeType.URL]: {
        sectionTitleKey: "home.dashboards.wizard.filters.multiIncludeExcludeUrl",
        textFieldComponent: (props) => (
            <FilterInputField
                {...props}
                filterType={multiIncludeExcludeType.URL}
                applyButtonText={translate("common.add")}
                placeholder={translate("dashboard.filters.multi.include.exclude.url.placeholder")}
            />
        ),
    },
    [multiIncludeExcludeType.TITLE]: {
        sectionTitleKey: "home.dashboards.wizard.filters.multiIncludeExcludeTitle",
        textFieldComponent: (props) => (
            <FilterInputField
                {...props}
                filterType={multiIncludeExcludeType.TITLE}
                applyButtonText={translate("common.add")}
                placeholder={translate("dashboard.filters.multi.include.exclude.title.placeholder")}
            />
        ),
    },
};

interface IMultiIncludeExcludeDashboardFilter {
    filterType: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType];
    initialRules: IDynamicFilterRule[];
    onMultiIncludeExcludeChange: (rules: IDynamicFilterRule[]) => void;
}

export const MultiIncludeExcludeDashboardFilter: FC<IMultiIncludeExcludeDashboardFilter> = ({
    filterType,
    initialRules,
    onMultiIncludeExcludeChange,
}) => {
    const typeConfig = typeConfigMap[filterType];
    const onAddRule = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", "Add rule");
    };

    return (
        <FlexColumn>
            <h5>
                <I18n>{typeConfig.sectionTitleKey}</I18n>
            </h5>
            <MultiIncludeExcludeFilter
                inputIconName="globe"
                initialRules={initialRules}
                maxItemsPerRule={MAX_KEYWORDS_PER_RULE}
                maxRules={MAX_RULES}
                ruleSeparatorText={translate(RULE_SEPARATOR_TEXT_KEY)}
                addRuleText={translate(ADD_RULE_TEXT_KEY)}
                primaryButtonText={translate(PRIMARY_BUTTON_TEXT_KEY)}
                secondaryButtonText={translate(SECONDARY_BUTTON_TEXT_KEY)}
                chipsSeparatorText={translate(CHIP_SEPARATOR_TEXT_KEY)}
                maxRulesBannerText={translate(MAX_RULES_BANNER_TEXT_KEY, { count: MAX_RULES })}
                maxItemsPerRuleBannerText={translate(MAX_ITEMS_PER_RULE_BANNER_TEXT_KEY, {
                    count: MAX_KEYWORDS_PER_RULE,
                })}
                autocompleteMaxItemsBannerText={translate(AUTOCOMPLETE_MAX_ITEMS_BANNER_TEXT_KEY, {
                    count: MAX_KEYWORDS_PER_RULE,
                })}
                onApply={onMultiIncludeExcludeChange}
                dropDownsOptions={getDropdownOptions(filterType)}
                textBoxWidth={240}
                onAddRule={onAddRule}
                withFooter={false}
                onUpdateRules={onMultiIncludeExcludeChange}
                TextFieldComponent={typeConfig.textFieldComponent}
            />
        </FlexColumn>
    );
};
